/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/qaInspectionMenu.ts
 * FILE_ID: QA-UI-INSPECTION-MENU-001
 * PURPOSE: Right-click menu for one QA list on the job tab.
 */
import { Copy, ExternalLink, FolderInput, Pencil, Trash2 } from 'lucide-react'
import type { ContextMenuItem } from '@quotech/ui'
import type { QaInspectionSummary } from '../../qa/qaApi'

export interface QaInspectionGroupChoice {
  id: string
  name: string
}

export function inspectionGroupChoices(
  inspections: readonly Pick<QaInspectionSummary, 'group_id' | 'group_name'>[],
): QaInspectionGroupChoice[] {
  const groups = new Map<string, string>()
  for (const row of inspections) {
    if (row.group_id) groups.set(row.group_id, row.group_name || 'Group')
  }
  return [...groups.entries()].map(([id, name]) => ({ id, name }))
}

export function buildQaInspectionMenu({
  groups,
  onOpenHub,
  onRename,
  onDuplicate,
  onDuplicateToJob,
  onCopy,
  onPaste,
  pasteLabel = null,
  onMove,
  onNewGroup,
  onDelete,
}: {
  groups: readonly QaInspectionGroupChoice[]
  onOpenHub: () => void
  onRename: () => void
  onDuplicate: () => void
  onDuplicateToJob: () => void
  onCopy?: () => void
  onPaste?: () => void
  pasteLabel?: string | null
  onMove: (groupId: string | null) => void
  onNewGroup: () => void
  onDelete: () => void
}): ContextMenuItem[] {
  // Reused from: packages/job-hub/src/jobs/qa/qaChecklistRowMenu.ts (ContextMenuItem builder)
  return [
    { id: 'open-hub', label: 'Open in QA hub', icon: ExternalLink, onSelect: onOpenHub },
    { id: 'rename', label: 'Rename', icon: Pencil, onSelect: onRename },
    { id: 'duplicate', label: 'Duplicate', icon: Copy, onSelect: onDuplicate },
    { id: 'duplicate-job', label: 'Duplicate onto another job', onSelect: onDuplicateToJob },
    ...(onCopy ? [
      { id: 'copy-qa', label: 'Copy QA', icon: Copy, onSelect: onCopy },
      {
        id: 'paste-qa',
        label: 'Paste QA',
        hint: pasteLabel ?? undefined,
        disabled: !pasteLabel || !onPaste,
        onSelect: onPaste,
      },
    ] : []),
    {
      id: 'move',
      label: 'Move to group',
      icon: FolderInput,
      submenu: [
        { id: 'ungrouped', label: 'Ungrouped', onSelect: () => onMove(null) },
        ...groups.map((group) => ({
          id: `group-${group.id}`,
          label: group.name,
          onSelect: () => onMove(group.id),
        })),
        { id: 'new-group', label: 'New group', onSelect: onNewGroup },
      ],
    },
    { id: 'delete', kind: 'destructive', label: 'Delete', icon: Trash2, onSelect: onDelete },
  ]
}
